// ============================================================================
// CONFIG SETTINGS PANEL
// ============================================================================
// Right pane of the Settings screen. Renders:
//   • Group header (name + description)
//   • Scope-type tabs (SYSTEM | ENTERPRISE | ENTITY | BUSINESS_UNIT | FINANCIAL_YEAR | VOUCHER_TYPE)
//   • Scope-target picker (only for non-SYSTEM scopes; hidden for SYSTEM)
//   • List of definitions with data-type-aware value editors + ScopeChip
//   • Save / Discard footer bar (visible only when there are pending changes)
//
// Scope semantics per plan D-9: 6-tier chain, most-specific first. This panel
// lets the admin author overrides at ANY level; ScopeChip shows where the
// currently-displayed value actually came from.
// ============================================================================

import React, { useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import ConfigSettingField from './ConfigSettingField';
import type {
  ConfigDefinitionDto,
  ConfigGroupDto,
  ConfigScopeDto,
  ConfigScopeType,
  EffectiveConfigEntry,
} from 'api/types/config.types';
import { isSingletonScopeType, type PendingEdit } from 'hooks/api/useConfigSettings';

// Scope-type tabs in most-specific → least-specific order (matches plan D-9).
// SYSTEM + ENTERPRISE are singletons (one row per tenant DB per ADR-0002) →
// no target picker + fields render immediately. Everything else needs a
// target UUID picked from the dropdown.
//
// FBP DISPLAY-ONLY: 'BUSINESS_UNIT' is labelled "Branch" here to match the
// term the main-layout header uses (Branch / FY selector). The framework's
// scope_type enum value stays 'BUSINESS_UNIT' so the DB column, API
// contract, and TypeScript literal stay domain-neutral — other adopting
// apps (CRS "Property", warehouse app "Warehouse", ...) can override the
// label without touching the framework. See LMMS "Eligible Weight" pattern.
// Icon keys resolved against fairbooks-web/src/icon-collection/icon-systems.tsx.
// If a key is retired from IconSystem, tabs still render (label-only fallback).
const SCOPE_TABS: {
  type: ConfigScopeType;
  label: string;
  iconKey: keyof typeof IconSystem;
  maxScopeLevel: number;
}[] = [
  { type: 'SYSTEM',         label: 'System',         iconKey: 'settings',    maxScopeLevel: 0 },
  { type: 'ENTERPRISE',     label: 'Enterprise',     iconKey: 'home',        maxScopeLevel: 1 },
  { type: 'ENTITY',         label: 'Entity',         iconKey: 'folder',      maxScopeLevel: 2 },
  { type: 'BUSINESS_UNIT',  label: 'Branch',         iconKey: 'dashboard',   maxScopeLevel: 3 },
  { type: 'FINANCIAL_YEAR', label: 'Financial Year', iconKey: 'calendar',    maxScopeLevel: 4 },
  { type: 'VOUCHER_TYPE',   label: 'Voucher Type',   iconKey: 'tag',         maxScopeLevel: 5 },
];

interface ConfigSettingsPanelProps {
  activeGroup: ConfigGroupDto | null;
  definitions: ConfigDefinitionDto[];
  effectiveEntries: EffectiveConfigEntry[];
  loading: boolean;

  activeScopeType: ConfigScopeType;
  onScopeTypeChange: (scopeType: ConfigScopeType) => void;
  activeScopeId: string | null;
  onScopeIdChange: (scopeId: string | null) => void;
  scopes: ConfigScopeDto[];
  scopesLoading: boolean;

  editedValues: Record<string, PendingEdit>;
  onValueChange: (configKey: string, value?: string, items?: string[]) => void;
  onResetValue: (configKey: string) => void;

  hasChanges: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

const ConfigSettingsPanel: React.FC<ConfigSettingsPanelProps> = ({
  activeGroup,
  definitions,
  effectiveEntries,
  loading,
  activeScopeType,
  onScopeTypeChange,
  activeScopeId,
  onScopeIdChange,
  scopes,
  scopesLoading,
  editedValues,
  onValueChange,
  onResetValue,
  hasChanges,
  saving,
  onSave,
  onDiscard,
}) => {
  // Only show scope tabs supported by at least one definition in this group —
  // no point showing "Voucher Type" if no definition allows depth ≥ 5.
  const availableTabs = useMemo(() => {
    if (definitions.length === 0) return SCOPE_TABS.slice(0, 2); // SYSTEM + ENTERPRISE fallback
    const maxDepth = Math.max(...definitions.map((d) => d.maxScopeLevel));
    return SCOPE_TABS.filter((t) => t.maxScopeLevel <= maxDepth);
  }, [definitions]);

  const scopeOptions: SelectOption[] = useMemo(
    () =>
      scopes.map((s) => ({
        value: s.id,
        label: s.displayName ?? `${s.scopeType}: ${s.scopeId ?? '—'}`,
      })),
    [scopes],
  );

  // Pair each definition with its resolved effective entry for rendering.
  const rows = useMemo(() => {
    return definitions
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder || a.configKey.localeCompare(b.configKey))
      .map((def) => ({
        definition: def,
        effective: effectiveEntries.find((e) => e.configKey === def.configKey),
      }));
  }, [definitions, effectiveEntries]);

  const handleScopeIdChange = useCallback(
    (val: string | string[] | null) => {
      onScopeIdChange(typeof val === 'string' ? val : null);
    },
    [onScopeIdChange],
  );

  if (!activeGroup) {
    return (
      <div className="config-settings-panel">
        <div className="config-empty">Select a group to view settings</div>
      </div>
    );
  }

  // Singletons (SYSTEM, ENTERPRISE) auto-select in the hook — no picker + no
  // "Select a X..." gate. Multi-target types still need the user to pick.
  const requiresTarget = !isSingletonScopeType(activeScopeType);
  const targetMissing = requiresTarget && !activeScopeId;

  // Display label for the current scope-type — pulled from SCOPE_TABS so the
  // FBP-specific "Branch" (instead of "Business Unit") label flows through
  // to the picker placeholder + empty-state message. Falls back to the
  // enum value only when a tab is missing (shouldn't happen).
  const activeScopeLabel =
    SCOPE_TABS.find((t) => t.type === activeScopeType)?.label ?? activeScopeType;

  return (
    <div className="config-settings-panel">
      {/* Header */}
      <div className="config-panel-header">
        <h2>{activeGroup.displayName}</h2>
        {activeGroup.description && <p>{activeGroup.description}</p>}
      </div>

      {/* Scope bar */}
      <div className="config-scope-bar">
        <div className="config-scope-toggle">
          {availableTabs.map((tab) => {
            // Resolve icon at render time — protects against a future
            // IconSystem retirement (bad key just renders label-only).
            const IconComp = IconSystem[tab.iconKey];
            return (
              <button
                key={tab.type}
                type="button"
                className={`config-scope-toggle-btn${activeScopeType === tab.type ? ' active' : ''}`}
                onClick={() => onScopeTypeChange(tab.type)}
                disabled={saving}
              >
                {IconComp && (
                  <span
                    className="config-scope-toggle-icon"
                    style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.35rem' }}
                  >
                    <IconComp size={14} />
                  </span>
                )}
                {tab.label}
              </button>
            );
          })}
        </div>

        {requiresTarget && (
          <div className="config-scope-select-row">
            <div className="config-scope-select">
              <SyutSelect
                key={activeScopeType}
                options={scopeOptions}
                value={activeScopeId ?? undefined}
                onChange={handleScopeIdChange}
                placeholder={
                  scopesLoading
                    ? 'Loading scopes...'
                    : scopeOptions.length === 0
                      ? `No ${activeScopeLabel} rows seeded yet`
                      : `Select a ${activeScopeLabel.toLowerCase()}...`
                }
                isClearable
                size="sm"
                isDisabled={scopesLoading || scopeOptions.length === 0}
              />
            </div>
          </div>
        )}
      </div>

      {/* Settings list */}
      <div className="config-settings-list">
        {loading ? (
          <div className="config-loading">Loading settings...</div>
        ) : targetMissing ? (
          <div className="config-empty">
            Select a {activeScopeLabel.toLowerCase()} to view / edit overrides.
          </div>
        ) : rows.length === 0 ? (
          <div className="config-empty">No definitions in this group.</div>
        ) : (
          rows.map(({ definition, effective }) => (
            <ConfigSettingField
              key={definition.configKey}
              definition={definition}
              effective={effective}
              editedValue={editedValues[definition.configKey]}
              activeScopeType={activeScopeType}
              onValueChange={onValueChange}
              onResetValue={onResetValue}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {(hasChanges || saving) && (
        <div className="config-footer-bar">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onDiscard}
            disabled={saving}
          >
            Discard
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConfigSettingsPanel;
